import { z } from 'zod';
import type { HedgeBookEntry } from '../api/hedge-book';
import type { RecordRollPayload } from '../api/executions';
import { decimalString, optionalText, todayIsoDate } from './form-helpers';
import { fxQuoteCurrency } from './fx-currency';

export const rollPairFormSchema = z.object({
  quantityMt: decimalString,
  executionDate: z.string().min(1, 'Required'),
  closeExecutedPrice: decimalString,
  openExecutedPrice: decimalString,
  closeContractReference: optionalText(200),
  openContractReference: optionalText(200),
  executedPriceCurrency: z.string().trim().length(3, 'ISO 4217 code'),
  brokerName: z.string().trim().min(1, 'Required'),
  // Blank for FX: the pair trades OTC, so the dialog hides the field and the
  // payload omits it rather than stamping the leg with an exchange.
  exchange: optionalText(50),
  notes: optionalText(1000),
});

export type RollPairFormValues = z.infer<typeof rollPairFormSchema>;

export function isFxEntry(entry: HedgeBookEntry): boolean {
  return entry.instrumentType === 'FX';
}

export function defaultRollPairValues(entry: HedgeBookEntry): RollPairFormValues {
  const isFx = isFxEntry(entry);
  return {
    quantityMt: entry.quantityMt,
    executionDate: todayIsoDate(),
    closeExecutedPrice: '',
    openExecutedPrice: '',
    closeContractReference: '',
    openContractReference: '',
    executedPriceCurrency: (isFx ? fxQuoteCurrency(entry.currencyPair) : 'USD') ?? 'USD',
    brokerName: 'StoneX',
    exchange: isFx ? '' : 'LME',
    notes: '',
  };
}

function trimmedOrUndefined(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

export function buildRollPairPayload(
  entry: HedgeBookEntry,
  values: RollPairFormValues,
): RecordRollPayload {
  const payload: RecordRollPayload = {
    hedgeRequestId: entry.id,
    executionDate: values.executionDate,
    quantityMt: values.quantityMt,
    closeExecutedPrice: values.closeExecutedPrice,
    openExecutedPrice: values.openExecutedPrice,
    executedPriceCurrency: values.executedPriceCurrency,
    brokerName: values.brokerName,
  };
  const exchange = trimmedOrUndefined(values.exchange);
  if (exchange) payload.exchange = exchange;
  const closeRef = trimmedOrUndefined(values.closeContractReference);
  if (closeRef) payload.closeContractReference = closeRef;
  const openRef = trimmedOrUndefined(values.openContractReference);
  if (openRef) payload.openContractReference = openRef;
  const notes = trimmedOrUndefined(values.notes);
  if (notes) payload.notes = notes;
  return payload;
}
